import { useState } from 'react';
import { convertDateToTimeZone, DateFormatter } from 'infrastructure/functions';
import { usePatientInfo } from 'infrastructure/hooks/patient/use-patient-info';
import { THRESHOLD_UNITS, Timezones } from 'infrastructure/enums';

import { hypervolemiaSymptoms } from '../templates/chf-weight';
import { generateChfWeightMessage } from '../blurbs';
import { getNursingInterventionValues } from '../helpers';

import type { IUseChfWeightProps } from '../types';
import type { IChfWeightValues } from 'components/molecules/rpm-alert-assessment/types';

export const useChfWeightAssessment = ({
  activityId,
  activityDate,
  values,
  patientGuid,
  addComment,
  closeModal,
  handleSubmit,
}: IUseChfWeightProps) => {
  const [openEmergencyModal, setOpenEmergencyModal] = useState<boolean>(false);
  const { patientInfo } = usePatientInfo({
    patientGuid,
  });

  const initialValues: IChfWeightValues = {
    // Q1
    patient: undefined,
    other: null,
    relationship: null,
    // Q2
    AOC: null,
    // Q3
    last: null,
    next: null,
    // Q4
    isOneMoreReading: null,
    bounds: null,
    repeatReadingWas: null,
    // Q5
    symptoms: null,
    symptomsOnset: null,
    emergencyCare: null,
    confirmedSymptoms: [],
    deniedSymptoms: [],
    checkSymptoms: true,
    // Q6
    isTakeMedications: null,
    prescribed: [],
    otherPrescribed: null,
    // Q7
    isTakePrnMedications: null,
    prnPrescribed: [],
    otherPrnMedications: null,
    // Q8
    confirmedFactors: [],
    deniedFactors: [],
    // Q19
    nursingIntervention: [],
    notEscalated: [],
    interventionDetails: null,
    // Q10
    verbalized: null,
    escalationToClinic: [],
  };

  const reading = `${values.weight} ${THRESHOLD_UNITS.Weight}`;
  const timeZone =
    patientInfo?.loginInfo?.timezone || Timezones['America/Chicago'];

  const dataInPatientTimeZone = convertDateToTimeZone(
    new Date(Number(activityDate)),
    Timezones[timeZone],
  );
  const dateOfReading = DateFormatter({
    date: dataInPatientTimeZone,
  });

  const symptomsList = hypervolemiaSymptoms;

  const emergentSymptoms = symptomsList
    .filter((item) => item.required)
    .map((item) => item.label);

  const preparedData = (data: IChfWeightValues) => {
    const medications = [...data.prescribed, data.otherPrescribed].filter(
      Boolean,
    );
    const prnMedications = [
      ...data.prnPrescribed,
      data.otherPrnMedications,
    ].filter(Boolean);

    const isSpeakWithPatient = data.patient === 'Patient';
    const lastDate = data.last
      ? DateFormatter({ date: new Date(data.last) })
      : null;
    const nextDate = data.next
      ? DateFormatter({ date: new Date(data.next) })
      : null;
    const nursingInterventionValues = getNursingInterventionValues(
      data?.nursingIntervention,
    );
    const confirmedEmergentSymptoms = data.confirmedSymptoms?.filter(
      (item: string) => emergentSymptoms.includes(item),
    );
    const confirmedSymptomsWithoutEmergent = data.confirmedSymptoms?.filter(
      (item: string) => !emergentSymptoms.includes(item),
    );

    return {
      // Did you speak to patient?
      Q1: {
        reading,
        dateOfReading,
        patient: data.patient,
        relationship: data.relationship,
        speakWithPatient: isSpeakWithPatient,
        speakWithOther: data.other,
      },
      // Mental Status Assessment
      Q2: {
        AOC: data.AOC,
      },
      // Appointments
      Q3: {
        last: lastDate,
        next: nextDate,
      },
      //
      Q4: {
        isOneMoreReading: data.isOneMoreReading ?? null,
        bounds: data.bounds ?? null,
        repeatReadingWas: data.repeatReadingWas ?? null,
      },
      // Signs and Symptoms of Congestive Heart Failure
      Q5: {
        confirmedSymptoms: confirmedSymptomsWithoutEmergent,
        deniedSymptoms: data.deniedSymptoms,
        isAllSymptomsDenied:
          data.deniedSymptoms?.length === symptomsList.length,
        confirmedEmergentSymptoms,
        symptomsOnset: data.symptomsOnset,
        emergencyModalAnswer: data.emergencyCare,
      },
      // Medications
      Q6: {
        isTakeMedications: data.isTakeMedications,
        medications,
        hasMedications: !!medications?.length,
      },
      // PRN medications
      Q7: {
        isTakePrnMedications: data.isTakePrnMedications,
        prnMedications,
        hasPrnMedications: !!prnMedications.length,
      },
      // Factors Assessment
      Q8: {
        confirmedFactors: data.confirmedFactors,
        deniedFactors: data.deniedFactors,
      },
      // Nursing Intervention
      Q9: {
        ...nursingInterventionValues,
        notEscalated: data.notEscalated,
        interventionDetails: data.interventionDetails,
        escalationToClinic: data.escalationToClinic,
      },
      Q10: {
        verbalized: data.verbalized ?? null,
      },
    };
  };

  const toggleEmergencyModal = () => setOpenEmergencyModal((prev) => !prev);

  const onSaveForm = (data: IChfWeightValues) => {
    const preparedValues = preparedData(data);
    const chfMessage = generateChfWeightMessage(preparedValues);

    handleSubmit(chfMessage);
    addComment(activityId);
    closeModal();
  };

  return {
    initialValues,
    openEmergencyModal,
    symptomsList,
    onSaveForm,
    toggleEmergencyModal,
  };
};
